"use client";

import { useEffect } from "react";
import { FileText, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PdfViewer } from "@/components/lazy-heavy-components";
import { PdfViewerToolbar } from "@/components/pdf-viewer-toolbar";
import { Button } from "@/components/ui/button";

export interface PdfViewerPageProps {
  url: string;
  fileName: string;
  onClose: () => void;
}

/** Standalone full-viewport PDF viewer used by chat pop-out URLs. */
export function PdfViewerPage({ url, fileName, onClose }: PdfViewerPageProps) {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = fileName;
    return () => {
      document.title = import.meta.env.APP_NAME ?? "Layer Nine";
    };
  }, [fileName]);

  return (
    <div className="flex h-screen min-h-0 flex-col bg-background text-foreground">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-semibold" title={fileName}>
            {fileName}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <PdfViewerToolbar fileUrl={url} fileName={fileName} />
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            aria-label={t("viewer.closeViewer")}
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>
      <PdfViewer url={url} fileName={fileName} className="min-h-0 flex-1" />
    </div>
  );
}
